<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { AlertTriangle } from 'lucide-vue-next'
import Dialog from '@/components/ui/Dialog.vue'
import Button from '@/components/ui/Button.vue'

interface Props {
  open: boolean
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  danger?: boolean
  /** Blocks confirm/cancel/dismiss and shows a spinner on the confirm button. */
  loading?: boolean
  /** Set false when the caller owns closing the dialog (e.g. ConfirmDialogHost). */
  closeOnConfirm?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  confirmText: undefined,
  cancelText: undefined,
  danger: false,
  loading: false,
  closeOnConfirm: true,
})

const emit = defineEmits<{
  (e: 'confirm'): void
  (e: 'cancel'): void
  (e: 'update:open', value: boolean): void
}>()

const { t } = useI18n()

const confirmLabel = computed(() => props.confirmText || t('common.confirm'))
const cancelLabel = computed(() => props.cancelText || t('common.cancel'))

const handleConfirm = () => {
  emit('confirm')
  if (props.closeOnConfirm) {
    emit('update:open', false)
  }
}

const handleCancel = () => {
  if (props.loading) return
  emit('cancel')
  emit('update:open', false)
}

const handleOpenChange = (value: boolean) => {
  if (props.loading) return
  emit('update:open', value)
}
</script>

<template>
  <Dialog
    :open="open"
    :title="title"
    :dismissible="!loading"
    @update:open="handleOpenChange"
  >
    <div class="flex gap-3">
      <div
        v-if="danger"
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger-500/15"
      >
        <AlertTriangle
          class="h-5 w-5 text-danger-400"
          aria-hidden="true"
        />
      </div>
      <p class="text-sm text-slate-400">
        {{ message }}
      </p>
    </div>

    <div class="mt-5 flex flex-wrap justify-end gap-2">
      <Button
        variant="secondary"
        :disabled="loading"
        @click="handleCancel"
      >
        {{ cancelLabel }}
      </Button>
      <Button
        :variant="danger ? 'destructive' : 'primary'"
        :loading="loading"
        @click="handleConfirm"
      >
        {{ confirmLabel }}
      </Button>
    </div>
  </Dialog>
</template>
